import React, { ChangeEvent, FormEvent, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { useProject } from "../contexts/ProjectContext";
import { getApiUrl } from "../utils/api";
import { authFetch } from "../utils/authFetch";
import "../App.css";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLingui } from "@lingui/react";
import { t } from '@lingui/core/macro';

const FEEDBACK_ATTACHMENT_EXTENSIONS = new Set([
  "html", "htm", "jpeg", "jpg", "m4v", "mov", "mp4", "pdf", "png", "tif", "tiff", "webm", "webp", "zip",
]);

type FeedbackResult = {
  kind: "success" | "error";
  message: string;
};

type ErrorResponse = {
  message?: string;
  warnings?: string[];
};

export default function Feedback(): React.ReactElement {
  useLingui();
  const navigate = useNavigate();
  const { selectedProject } = useProject();

  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [result, setResult] = useState<FeedbackResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const selectedAttachment = event.target.files?.[0] ?? null;
    if (!selectedAttachment) {
      setAttachment(null);
      return;
    }
    const extension = selectedAttachment.name.split(".").pop()?.toLowerCase() ?? "";
    if (!FEEDBACK_ATTACHMENT_EXTENSIONS.has(extension)) {
      event.target.value = "";
      setAttachment(null);
      setResult({ kind: "error", message: t`This attachment type is not supported.` });
      return;
    }
    setAttachment(selectedAttachment);
    setResult(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setResult({ kind: "error", message: t`Please enter feedback before submitting.` });
      return;
    }

    setIsSubmitting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("message", trimmedMessage);
      formData.append("projectAttachment", selectedProject ? "both" : "none");
      if (selectedProject) {
        formData.append("projectId", selectedProject.id);
      }
      if (attachment) {
        formData.append("attachment", attachment);
      }

      const response = await authFetch(`${getApiUrl()}/feedback`, {
        method: "POST",
        body: formData,
      });

      const data: ErrorResponse = await response.json().catch((): ErrorResponse => ({}));

      if (!response.ok) {
        throw new Error(data.message || t`Feedback submission failed.`);
      }

      setMessage("");
      setAttachment(null);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      const warning = data.warnings?.[0];
      setResult({
        kind: "success",
        message: warning ? `${t`Thanks. Your feedback was submitted.`} ${warning}` : t`Thanks. Your feedback was submitted.`,
      });
    } catch (error) {
      setResult({
        kind: "error",
        message: error instanceof Error ? error.message : t`Feedback submission failed.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="feedback-page">
      <NavBar />
      <main className="feedback-shell">
        <div className="feedback-content">
          <button
            type="button"
            className="editor-return-button"
            onClick={() => navigate('/app')}
          >
            <ArrowBackIcon style={{ fontSize: '18px' }} />
            {t`Back to editor`}
          </button>
          <form className="feedback-card" onSubmit={handleSubmit}>
            <div className="feedback-header">
              <h1>{t`Send feedback`}</h1>
              <p>{t`Share an issue, an idea, or anything that could make FinTitles better.`}</p>
            </div>

            <label className="feedback-field">
              <span>{t`Feedback`}</span>
              <textarea
                required
                dir="auto"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t`Tell us what happened or what would make this better.`}
                rows={7}
                maxLength={5000}
              />
            </label>

            <label className="feedback-field">
              <span>{t`Attachment (optional)`}</span>
              <input
                className="feedback-file-input"
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.pdf,.html,.htm,.tif,.tiff,.zip,.mp4,.mov,.m4v,.webm"
                aria-label={t`Attachment (optional)`}
                ref={attachmentInputRef}
                onChange={handleAttachmentChange}
              />
              <small>{t`Images, PDF, HTML, TIFF or TIFF ZIP, and video (MP4, MOV, M4V, WebM).`}</small>
            </label>

            <button className="feedback-submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? t`Submitting...` : t`Submit`}
            </button>

            <p className="feedback-data-note">
              {t`* We use your project data to help find and fix the problem.`}
            </p>

            {result && (
              <p className={`feedback-message ${result.kind}`} role="status">
                {result.message}
              </p>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
